// Kapatılabilir sekme çubuğu — ref_files/btys-erp-01-stok-karti.html'deki
// tabs/drawTabs/activate/openPane/closeTab davranışının karşılığı.
//
// Referans mantığı: liste sekmesi daima ilk sırada ve kapatılamaz; her "tam
// sayfa" açılışı yeni bir .pane ekler ve tabbar'a kapatılabilir sekme koyar.
// Aktif olmayan pane DOM'da kalır ama .on sınıfı olmadan gizlidir — biz de
// aynısını yapıyoruz, böylece sekmeler arası geçişte form durumu kaybolmuyor.

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react'

export type Sekme = {
  id: string
  baslik: string
  icerik: ReactNode
}

type Baglam = {
  sekmeler: Sekme[]
  aktif: string
  /** Zaten açık sekmeyi yeniden açmaz, ona geçer (referans openPane davranışı). */
  ac: (sekme: Sekme) => void
  gecis: (id: string) => void
  kapat: (id: string) => void
  listeBasligi: string
}

const SekmeBaglami = createContext<Baglam | null>(null)

export const LISTE = 'list'

export function SekmeSaglayici({
  listeBasligi,
  children,
}: {
  listeBasligi: string
  children: ReactNode
}) {
  const [sekmeler, setSekmeler] = useState<Sekme[]>([])
  const [aktif, setAktif] = useState<string>(LISTE)

  const ac = useCallback((sekme: Sekme) => {
    setSekmeler((s) => (s.some((x) => x.id === sekme.id) ? s : [...s, sekme]))
    setAktif(sekme.id)
  }, [])

  const kapat = useCallback((id: string) => {
    setSekmeler((s) => s.filter((x) => x.id !== id))
    // Referans closeTab daima listeye döner.
    setAktif(LISTE)
  }, [])

  const deger = useMemo<Baglam>(
    () => ({ sekmeler, aktif, ac, gecis: setAktif, kapat, listeBasligi }),
    [sekmeler, aktif, ac, kapat, listeBasligi],
  )

  return <SekmeBaglami.Provider value={deger}>{children}</SekmeBaglami.Provider>
}

export function useSekmeler() {
  const b = useContext(SekmeBaglami)
  if (!b) throw new Error('useSekmeler yalnız SekmeSaglayici içinde kullanılabilir.')
  return b
}

/** Kabuk'un <header> ile <main> arasına koyduğu .tabbar. */
export function SekmeCubugu() {
  const b = useContext(SekmeBaglami)
  if (!b) return <div className="tabbar" />

  return (
    <div className="tabbar">
      <button
        className={b.aktif === LISTE ? 'tab on' : 'tab'}
        type="button"
        onClick={() => b.gecis(LISTE)}
      >
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M8 6h13M8 12h13M8 18h13M3 6h.01M3 12h.01M3 18h.01" />
        </svg>
        {b.listeBasligi}
      </button>
      {b.sekmeler.map((s) => (
        <button
          key={s.id}
          className={b.aktif === s.id ? 'tab on' : 'tab'}
          type="button"
          onClick={() => b.gecis(s.id)}
        >
          {s.baslik}
          <span
            className="tx"
            role="button"
            aria-label="Sekmeyi kapat"
            onClick={(e) => {
              e.stopPropagation()
              b.kapat(s.id)
            }}
          >
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </span>
        </button>
      ))}
    </div>
  )
}

/**
 * Liste panosu + açık sekmelerin panoları. Referansta olduğu gibi hepsi DOM'da
 * durur, yalnız aktif olan `.on` alır.
 */
export function SekmePanolari({ liste }: { liste: ReactNode }) {
  const b = useContext(SekmeBaglami)
  if (!b) return <>{liste}</>

  return (
    <>
      <section className={b.aktif === LISTE ? 'pane on' : 'pane'}>{liste}</section>
      {b.sekmeler.map((s) => (
        <section key={s.id} className={b.aktif === s.id ? 'pane on' : 'pane'}>
          {s.icerik}
        </section>
      ))}
    </>
  )
}
